import { z } from 'zod';

import { BaseToolResultSchema } from './baseResult';

export const FontCandidateSchema = z.object({
  family: z.string().describe('Font family name'),
  similarity: z.number().describe('Similarity score (0–1)'),
  variant: z.string().describe('Font weight+style variant'),
});

export const GroupMemberSchema = z.object({
  height: z.number().optional().describe('Layer height'),
  id: z.string().describe('Group identifier — all layers sharing this ID are composited together'),
  width: z.number().optional().describe('Layer width'),
  x: z.number().optional().describe('X offset within the group'),
  y: z.number().optional().describe('Y offset within the group'),
  zIndex: z.number().optional().describe('Stacking order (0 = bottom)'),
});

export const TextNodeDataSchema = z.object({
  fontCandidates: z.array(FontCandidateSchema).optional().describe('Ranked font candidates from font detection'),
  fontColor: z.string().describe('Text color as hex string'),
  fontFamily: z.string().describe('Font family name'),
  fontFileUrl: z.string().optional().describe('URL to font file when source is "file"'),
  fontSize: z.number().describe('Font size in pixels'),
  fontSource: z.enum(['builtIn', 'file']).optional().describe('Font source type'),
  fontVariant: z.string().optional().describe('Font weight+style variant (e.g., "700", "italic")'),
  letterSpacing: z.number().optional().describe('Letter spacing in pixels'),
  lineHeight: z.number().optional().describe('Line height multiplier'),
  text: z.string().describe('The text content'),
  textAlign: z.enum(['center', 'left', 'right']).describe('Horizontal text alignment'),
});

export const ImageResultSchema = z.object({
  editUrl: z.string().describe('URL of the edited image'),
  fileName: z.string().optional().describe('Short descriptive label (2–5 words)'),
  group: GroupMemberSchema.optional().describe('Group membership for layered compositions'),
});

export const TextLayerResultSchema = z.object({
  fileName: z.string().optional().describe('Short descriptive label (2–5 words)'),
  group: GroupMemberSchema.optional().describe('Group membership for layered compositions'),
  textNode: TextNodeDataSchema.describe('Text node data for an editable text layer'),
  type: z.literal('text').describe('Discriminator for text layer results'),
});

export const FileEditCompleteArgsSchema = z.object({
  results: z
    .array(z.union([TextLayerResultSchema, ImageResultSchema]))
    .min(1)
    .describe('Array of image results and/or text layer results'),
});

export type FileEditCompleteArgs = z.infer<typeof FileEditCompleteArgsSchema>;

export const FileEditCompleteImageEditSchema = z.object({
  fileName: z.string().optional(),
  group: GroupMemberSchema.optional(),
  url: z.string(),
});

export type FileEditCompleteImageEdit = z.infer<typeof FileEditCompleteImageEditSchema>;

export const FileEditCompleteTextEditSchema = z.object({
  fileName: z.string().optional(),
  group: GroupMemberSchema.optional(),
  textNode: TextNodeDataSchema,
  type: z.literal('text'),
});

export type FileEditCompleteTextEdit = z.infer<typeof FileEditCompleteTextEditSchema>;

export const FileEditCompleteResultSchema = BaseToolResultSchema.extend({
  edits: z.array(z.union([FileEditCompleteTextEditSchema, FileEditCompleteImageEditSchema])).min(1),
  editUrl: z.string().optional(),
  message: z.string(),
  prompt: z.object({
    messageId: z.string(),
    runId: z.string(),
    text: z.string(),
  }),
});

export type FileEditCompleteResult = z.infer<typeof FileEditCompleteResultSchema>;

export type FileEditCompleteEdit = FileEditCompleteImageEdit | FileEditCompleteTextEdit;

export const isImageEdit = (edit: FileEditCompleteEdit): edit is FileEditCompleteImageEdit => 'url' in edit;

export const FILE_EDIT_COMPLETE_TOOL_NAME = 'file_edit_complete';

export const FILE_EDIT_COMPLETE_TOOL_DESCRIPTION =
  'Signal that image editing is complete and deliver results to the user. Accepts an array of image and/or text layer results.';

export const FILE_EDIT_COMPLETE_TOOL = {
  description: FILE_EDIT_COMPLETE_TOOL_DESCRIPTION,
  name: FILE_EDIT_COMPLETE_TOOL_NAME,
  parameters: FileEditCompleteArgsSchema.toJSONSchema(),
};
